import type { Breadcrumb } from "@sentry/react";
import { isAxiosError } from "axios";

// Sentry turns every console.* call into a breadcrumb and keeps the raw
// arguments. The app logs failed requests as `console.error("...", error)`,
// and an AxiosError carries the bearer token (config.headers) and whatever the
// user submitted (config.data). Neither belongs on a dashboard the whole
// team can read. Keep what diagnoses the failure — status and the backend's
// own message — and drop the rest.
export function scrubAxiosErrorBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (breadcrumb.category !== "console") return breadcrumb;

  const args = breadcrumb.data?.arguments;
  if (!Array.isArray(args)) return breadcrumb;

  const index = args.findIndex((arg) => isAxiosError(arg));
  if (index === -1) return breadcrumb;

  const error = args[index];
  // The backend is not consistent about the field name.
  const body = error.response?.data as
    { message?: string; error?: string } | undefined;
  const summary = {
    status: error.response?.status,
    message: body?.message ?? body?.error ?? error.message,
  };

  const scrubbedArgs = [...args];
  scrubbedArgs[index] = summary;

  // `message` is what the Sentry UI shows in the breadcrumb list; rebuild it
  // so the backend's reason is visible at a glance. Every call site in the
  // app passes a string label first, so that covers them all.
  const label = typeof args[0] === "string" ? args[0] : undefined;
  const statusPrefix = summary.status ? `[${summary.status}] ` : "";
  const message = label
    ? `${label} ${statusPrefix}${summary.message}`
    : breadcrumb.message;

  return {
    ...breadcrumb,
    message,
    data: { ...breadcrumb.data, arguments: scrubbedArgs },
  };
}
